import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { logout as logoutApi } from "../services/apiService";
import { logout as clearAuth } from "../utils/auth";

function LogoutConfirmModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setError("");
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError("");
    setIsSubmitting(true);

    try {
      await logoutApi();
      clearAuth();
      onSuccess?.();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          t("dashboard.logout.failed")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="settings-modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        className="settings-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-confirm-title"
      >
        <div className="settings-modal__header">
          <h2 id="logout-confirm-title" className="settings-modal__title">
            {t("dashboard.logout.title")}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label={t("dashboard.common.close")}
          >
            &times;
          </button>
        </div>

        <p className="settings-modal__subtitle">{t("dashboard.logout.message")}</p>

        {error ? (
          <p className="settings-form-status settings-form-status--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="settings-modal__actions">
          <button
            type="button"
            className="settings-btn settings-btn--outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dashboard.common.no")}
          </button>
          <button
            type="button"
            className="settings-btn settings-btn--primary"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("dashboard.logout.loggingOut") : t("dashboard.common.yes")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LogoutConfirmModal;
